<template>
  <v-card min-height="60vh" id="account-detail">
    <v-card-title :class="this.mobileCompatible ? 'primary pa-0 flex-nowrap' : 'primary'">
      <Icon
          v-if="!loading"
          iconColor="tertiary"
          dataTestId="account-detail-isexternal"
          :icon="account.isExternal ? 'mdi-domain' : 'mdi-home'"
          :small="false"
          :tooltipText="account.isExternal ? $t('external') : $t('internal')"
          style="line-height: 0;"
        />
      <DialogWithTabsTitle
      @menu-clicked="navDrawer = !navDrawer"
      :mobileCompatible="mobileCompatible"
      :titleText="accountName"
      hideRefresh
      :tabs="tabs"
      @tab-clicked="onTabClicked"
      :enlargeTabs="enlargeNavigationTabs"
      v-if="!loading"
      >
        <template #prepend-base-actions>
          <v-menu bottom left>
            <template #activator="{ on }">
              <v-icon v-on="on" color="white">mdi-dots-vertical</v-icon>
            </template>
            <v-list>
              <v-list-item v-for="(ha, index) in headerActions" :key="`${ha.text}-${index}`" @click="ha.action(account)" :disabled="ha.disabled " :id="`account-detail-context-menu-${kebabCase(ha.text)}`">
                <v-avatar left>
                  <v-icon color="black" :disabled="ha.disabled">{{ha.icon}}</v-icon>
                </v-avatar>
                <v-list-item-title class="subtitle-1">{{ $t(ha.text) }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </DialogWithTabsTitle>

    </v-card-title>
    <v-card-text :class="mobileCompatible ? 'pa-0' : ''">
      <v-container fluid>
        <v-row v-if="loading" justify="center" align="center">
          <v-progress-circular indeterminate color="primary"/>
        </v-row>
        <v-row v-if="!loading">
          <v-col :class="mobileCompatible ? 'pa-0' : ''">
            <v-tabs v-model="currentTab" v-if="!mobileCompatible">
              <v-tab v-for="(item, index) in tabs" :key="index" :value="index" :id="`account-detail-tab-${kebabCase(item)}`">
                <span>
                  <v-icon v-if="$vuetify.breakpoint.xl">{{ item.icon }}</v-icon>
                  {{ item }}
                  <Icon
                  v-if="item === $t('contactInfo') && invalidContactEmail"
                  :small="false"
                  icon="mdi-email-alert-outline"
                  iconColor="error"
                  :tooltipText="$t('invalidContactEmail')"/>
                </span>
              </v-tab>
            </v-tabs>
            <v-window v-model="currentTab" v-if="!loading" touchless>

              <v-window-item> <!-- Properties -->
                <v-row :class=" `py-4 ${this.$vuetify.breakpoint.mdAndDown? 'mx-2' : ''}`">
                  <v-col>
                    <DetailCard :title="$t('general')" :style="propertyCardStyle">
                      <v-row dense justify="end">
                        <v-col cols="12" sm="6" md="5">
                          <span>{{ $t('code') }}:</span>
                        </v-col>
                        <v-col cols="auto" sm="6" md="7">
                          <span>{{ code }}</span>
                        </v-col>
                        <v-col cols="12" sm="6" md="5">
                          <span>{{ $t('exportCode') }}:</span>
                        </v-col>
                        <v-col cols="auto" sm="6" md="7">
                          <span>{{ exportCode }}</span>
                        </v-col>
                        <v-col cols="12" sm="6" md="5">
                          <span>{{ $t('landownerType') }}:</span>
                        </v-col>
                        <v-col cols="auto" sm="6" md="7">
                          <span>{{ account.landownerType?.name ?? $t('notSpecified') }}</span>
                        </v-col>
                        <v-col cols="12" sm="6" md="5">
                          <span>{{ $t('settlementDeliveryMode') }}:</span>
                        </v-col>
                        <v-col cols="auto" sm="6" md="7">
                          <span>{{ SettlementDeliveryMode.fromInt(account.settlementDeliveryMode) }}</span>
                        </v-col>
                        <v-col cols="12" sm="6" md="5">
                          <span>{{ $t('accountTimezone') }}:</span>
                        </v-col>
                        <v-col cols="auto" sm="6" md="7">
                          <span>{{ timezoneOffset ?? $t('none') }}</span>
                        </v-col>
                      </v-row>
                    </DetailCard>
                  </v-col>

                  <v-col v-if="account.receiverSpot">
                    <DetailCard :title="$t('receiver')" :style="propertyCardStyle">
                      <template #title>
                        <Icon icon="receiver" :small="false"/>
                      </template>
                      <template #title.append>
                        <Icon icon="mdi-map" :small="false" @icon-clicked="onReceiverMapClicked"/>
                      </template>
                      <v-row dense>
                        <v-col cols="12">
                          <span>
                            <v-row>
                              <v-col cols="12" md="6">
                                <v-text-field
                                dense
                                :label="$t('latitude')"
                                class="mb-n6"
                                v-model="account.receiverSpot.latitude"
                                readonly
                                outlined
                                />
                              </v-col>
                              <v-col cols="12" md="6">
                                <v-text-field
                                dense
                                class="mb-n6"
                                :label="$t('longitude')"
                                v-model="account.receiverSpot.longitude"
                                readonly
                                outlined/>
                              </v-col>
                              <v-col cols="12">
                                <v-text-field
                                dense
                                :label="$t('pickupRadius')"
                                class="mb-n6"
                                v-model="pickupRadius"
                                readonly
                                outlined
                                color="black"/>
                              </v-col>
                            </v-row>
                          </span>
                        </v-col>
                      </v-row>
                    </DetailCard>
                  </v-col>

                  <v-col v-if="penalty">
                    <DetailCard :title="$t('penalty')" :style="propertyCardStyle">
                      <v-row dense>
                        <v-col cols="12" v-if="penalty">
                          <v-data-table
                          :items="penalty.thresholds"
                          :headers="penaltyHeaders"
                          mobile-breakpoint="0"
                          dense
                          hide-default-footer>
                            <template #item.threshold="{item}">
                              {{ numberWithCommas(item.threshold) }}
                            </template>
                            <template #item.penaltyAmount="{item}">
                              {{ formatMoney(item.penaltyAmount) }}
                            </template>
                          </v-data-table>
                        </v-col>
                        <v-col v-else>
                          <span class="pt-4">{{ $t('noneSpecified') }}</span>
                        </v-col>
                      </v-row>
                    </DetailCard>
                  </v-col>

                  <v-col v-if="account.accountTags && account.accountTags.length > 0">
                    <DetailCard :title="$t('tags')" :style="propertyCardStyle">
                      <v-row dense>
                        <v-col cols="12">
                          <v-chip v-for="(tag) in account.accountTags" :key="`${tag.tagId}`" class="mr-1 mb-1">
                            {{tag.value}}
                          </v-chip>
                          <span v-if="!(account?.accountTags?.length > 0)" data-testid="account-details-no-tag">{{$t('noneSpecified')}}</span>
                        </v-col>
                      </v-row>
                    </DetailCard>
                  </v-col>

                  <v-col v-if="account.isExternal">
                    <DetailCard :title="$t('options')" :style="propertyCardStyle">
                      <v-row dense>
                        <v-col cols="12" v-if="account.isExternal">
                          <v-row align="center" dense>
                            <v-col cols="auto">
                              <Icon
                                :icon="account.incursQuota ? 'mdi-check' : 'mdi-close'"
                                :iconColor="account.incursQuota ? 'success' : 'error'"
                                :tooltipText="account.incursQuota ? $t('enabled') : $t('disabled')"
                                :small="false"
                              />
                            </v-col>
                            <v-col>
                              {{ $t('incursQuota') }}
                            </v-col>
                          </v-row>
                        </v-col>
                        <v-col cols="12" v-if="!account.accountId || account.isExternal">
                          <v-row align="center" dense>
                            <v-col cols="auto">
                              <Icon
                                :icon="account.requireBol ? 'mdi-check' : 'mdi-close'"
                                :iconColor="account.requireBol ? 'success' : 'error'"
                                :tooltipText="account.requireBol ? $t('enabled') : $t('disabled')"
                                :small="false"
                              />
                            </v-col>
                            <v-col>
                              {{ $t('requireBol') }}
                            </v-col>
                          </v-row>
                        </v-col>
                      </v-row>
                    </DetailCard>
                  </v-col>
                </v-row>
              </v-window-item>

              <v-window-item> <!-- Contact Info -->
                <v-row class="ma-0">
                  <v-col cols="12">
                    <span class="subtitle-1">
                      {{$t('contacts')}}
                    </span>
                  </v-col>
                  <v-col cols="12" md="6" lg="4" v-for="(contact, index) in account.contacts" :key="`contact-${index}`">
                    <ContactCard
                    :showCrud="false"
                    :contact="contact"/>
                  </v-col>
                  <v-col v-if="account?.contacts?.length < 1">
                    <span>{{ $t('noneSpecified') }}</span>
                  </v-col>
                  <v-col cols="12">
                    <span class="subtitle-1">
                      {{$t('addresses')}}
                    </span>
                  </v-col>
                  <v-col cols="12" lg="4" v-for="(address, index) in account.addresses" :key="`address-${index}`">
                    <AddressCard
                    :address="address"
                    :showEdit="false"
                    :showDelete="false"
                    showType
                    />
                  </v-col>
                  <v-col cols="12" v-if="account.addresses.length === 0">
                    <span>{{$t('noneSpecified')}}</span>
                  </v-col>
                  <v-col cols="12">
                    <span class="subtitle-1">
                      {{$t('driverDetails')}}
                    </span>
                  </v-col>
                  <template v-if="account.driverDetail?.trailerIdentifiers?.length > 0">
                    <v-col cols="12" md="6" lg="4" v-for="(ti, index) in account.driverDetail.trailerIdentifiers" :key="`ti-${index}`">
                      <TrailerIdentifierCard
                        :trailerIdentifier="ti"
                        :showCrud="false"/>
                    </v-col>
                  </template>
                  <template v-if="account.driverDetail?.driversLicenses?.length > 0">
                    <v-col cols="12" md="6" lg="4" v-for="(license, index) in account.driverDetail.driversLicenses" :key="`license-${index}`">
                      <DriversLicenseCard
                        :showCrud="false"
                        :license="license"/>
                    </v-col>
                  </template>
                  <v-col v-else>
                    <span>{{ $t('noneSpecified') }}</span>
                  </v-col>
                </v-row>
              </v-window-item>

              <v-window-item> <!-- Advances -->
                <v-col v-if="mobileCompatible" cols="12">
                  <span class="subtitle-1">
                    {{$t('advances')}}
                  </span>
                </v-col>
                <v-col v-if="!loading">
                  <AccountAdvances
                    :account="account"
                    accountDetail
                  />
                </v-col>
              </v-window-item>

              <v-window-item> <!-- Insurance -->
                <v-col v-if="hasInsurancePolicy">
                  <v-row v-if="account.insurancePolicies?.length > 0">
                    <v-col v-if="mobileCompatible" cols="12">
                      <span class="subtitle-1">
                        {{ $t('insurancePolicies') }}
                      </span>
                    </v-col>
                    <v-col v-for="(insurancePolicy, index) in orderedInsurancePolicies" :key="`policy-${index}`" cols="12" md="6" lg="4">
                      <InsuranceCard
                      :showCrud="false"
                      :insurancePolicy="insurancePolicy"
                      :file="filesByInsurancePolicy(insurancePolicy)"
                      />
                    </v-col>
                  </v-row>
                  <v-row v-else>
                    <v-col cols="4">
                      <InsuranceCard
                      :showCrud="false"
                      :insurancePolicy="{name: $t('defaultInsurance'), expirationDate: account.insuranceExpiryDate}"/>
                    </v-col>
                  </v-row>
                </v-col>
                <v-col v-else>
                  <v-row>
                    <v-col v-if="mobileCompatible" cols="12">
                      <span class="subtitle-1">
                        {{ $t('insurancePolicies') }}
                      </span>
                    </v-col>
                    <v-col>
                      <span>{{ $t('noneSpecified') }}</span>
                    </v-col>
                  </v-row>
                </v-col>
              </v-window-item>

              <v-window-item> <!-- Certifications -->
                <v-col cols="12">
                  <v-row>
                    <v-col v-if="mobileCompatible" cols="12">
                      <span class="subtitle-1">{{$t('certifications')}}</span>
                    </v-col>
                    <v-col>
                      <AccountCertifications :accountId="accountId" :files="filesForCertifications()"/>
                    </v-col>
                  </v-row>
                </v-col>
              </v-window-item>

              <v-window-item>
                <v-col cols="12" class="pa-0">
                  <v-row>
                    <v-col v-if="mobileCompatible" cols="12">
                      <span class="subtitle-1">{{ $t('payments') }}</span>
                    </v-col>
                    <v-col class="py-0 px-2">
                      <AccountPayments :accountId="accountId"/>
                    </v-col>
                  </v-row>
                </v-col>
              </v-window-item>

              <v-window-item>
                <v-col cols="12" class="pa-0">
                  <v-row>
                    <v-col v-if="mobileCompatible" cols="12">
                      <span class="subtitle-1">{{ $t('ticketBooks') }}</span>
                    </v-col>
                    <v-col>
                      <v-container fluid>
                        <AccountTicketBooks
                        readonly
                        :loading="ticketBooksLoading"
                        :ticketBooks="ticketBooks"
                        :accountId="accountId"
                        @refresh-ticket-books="refreshTicketBooks"
                      />
                      </v-container>
                    </v-col>
                  </v-row>
                </v-col>
              </v-window-item>

              <v-window-item>
                <AccountScalingSpecs
                v-if="!scalingImporting"
                :account="account"/>
              </v-window-item>

              <v-window-item v-if="showEudrTab"> <!-- EUDR (external accounts only) -->
                <AccountEudrTab :account="account"/>
              </v-window-item>
            </v-window>
          </v-col>
        </v-row>

        <Dialog :stateId="dialogId" @close="closeDialog" @click:outside="closeDialog" @dialog-closing="closeDialog">
          <AccountMap
            v-if="mapOpen"
            :propLocation="account.receiverSpot"
            :propRadius="account.receiverRadius"
            :crud="false"
            @close="closeDialog"
          />

          <ScalingImportResultsDialog
            v-if="scalingImportResultsOpen"
            :results="scalingImportResults"
            @close="closeScalingImportResults"
          />
        </Dialog>

        <input
          ref="scalingFileInput"
          type="file"
          accept=".json,application/json"
          style="display: none"
          @change="onScalingFileSelected"
        >
      </v-container>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { utcToLocalDate } from '@/utils/DateFormatter.js'
import { generateAccountIdSheetPDF, getAccountQrCodeData } from '@/utils/pdf-formatters/AccountIdentification'
import { getLegnaQrCodeUrl } from '@/utils/LegnaQrCode'
import { UserClaims, SettlementDeliveryMode, AccountFileCategory, EmailStatus } from '@/utils/Enumerations'
import { userAssignedClaim } from '@/utils/ClaimUtility'
import moment from 'moment'
import { accountFilesClient, accountBolClient } from '@/utils/AccountFiles'
import timezones from '@/components/settings-components/company-config/timezones.json'
import { overweightThresholdHeaders } from '@/headers/Account'
import { formatMoney, numberWithCommas } from '@/utils/NumericMutations.js'
import { metersToMilesDiscrete } from '@/utils/RadiusHelpers'
import { saveAs } from 'file-saver'
import { MOBILE_BREAKPOINT } from '@/utils/DeviceUtility'
import { kebabCase } from 'lodash'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { exportScalingSpecs, parseAndVerifyImportFile, importScalingSpecs } from '@/utils/AccountScalingIO'

export default {
  name: 'AccountDetail',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    ContactCard: () => import('@/components/account/contacts/ContactCard.vue'),
    AddressCard: () => import('@/components/account/address/AddressCard.vue'),
    DriversLicenseCard: () => import('@/components/account/driver-details/DriversLicenseCard.vue'),
    TrailerIdentifierCard: () => import('@/components/account/driver-details/TrailerIdentifierCard.vue'),
    AccountAdvances: () => import('@/components/account/advances/AccountAdvances.vue'),
    DialogWithTabsTitle: () => import('@/components/core/DialogWithTabsTitle.vue'),
    InsuranceCard: () => import('@/components/account/Insurance/InsuranceCard.vue'),
    AccountCertifications: () => import('@/components/account/certifications/AccountCertifications.vue'),
    AccountPayments: () => import('@/components/account/account-payables/AccountPayments.vue'),
    AccountTicketBooks: () => import('@/components/account/ticketBooks/AccountTicketBooks.vue'),
    DetailCard: () => import('@/components/core/DetailCard.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    AccountMap: () => import('@/components/account/AccountMap.vue'),
    AccountScalingSpecs: () => import('@/components/account/account-scaling/AccountScalingSpecs.vue'),
    ScalingImportResultsDialog: () => import('@/components/account/account-scaling/ScalingImportResultsDialog.vue'),
    AccountEudrTab: () => import('@/components/account/account-eudr/AccountEudrTab.vue')
  },

  props: {
    accountId: {
      type: Number,
      required: true
    }
  },

  data: () => ({
    account: undefined,
    loading: false,
    currentTab: 0,
    mobileCompatible: false,
    navDrawer: false,
    enlargeNavigationTabs: false,
    files: undefined,
    billOfLading: undefined,
    penalty: undefined,
    SettlementDeliveryMode,
    pickupRadius: '',
    ticketBooks: [],
    ticketBooksLoading: false,
    archivedTicketBooks: undefined,
    dialogId: uniqueDialogId('account-detail-dialog'),
    mapOpen: false,
    scalingImportResults: [],
    scalingImportResultsOpen: false,
    scalingExporting: false,
    scalingImporting: false
  }),

  computed: {
    ...mapGetters('user', ['companyInfo']),
    accountName () {
      if (!this.account) return ''
      return this.account.name
    },
    code () {
      if (!this.account) return ''
      return this.account.code || this.$t('notSpecified')
    },
    exportCode () {
      if (!this.account) return ''
      return this.account.exportCode || this.$t('notSpecified')
    },
    timezoneOffset () {
      if (!this.account) return ''
      return timezones.find(tz => Number(tz.offset) === this.account.timeZoneOffset)?.name
    },
    insuranceExpiryDate () {
      if (!this.account) return ''
      return this.account.insuranceExpiryDate ? utcToLocalDate(this.account.insuranceExpiryDate) : this.$t('notSpecified')
    },
    orderedInsurancePolicies () {
      return [...this.account.insurancePolicies].sort((a, b) => {
        const aDate = moment.utc(a.expirationDate)
        const bDate = moment.utc(b.expirationDate)
        return (moment.duration(aDate.diff(bDate)).asDays())
      })
    },
    headerActions () {
      const actions = (this.account) ? [
        {
          text: 'editAccount',
          icon: 'mdi-pencil',
          action: a => this.$emit('edit-account', a),
          disabled: !userAssignedClaim(UserClaims.AccountManager)
        },
        {
          text: 'showQrCode',
          icon: 'mdi-qrcode',
          action: this.showQrCode,
          disabled: !this.account.isExternal
        },
        {
          text: 'showDriverQrCode',
          icon: 'mdi-qrcode-scan',
          action: this.showDriverQrCode,
          disabled: !this.account.isExternal
        },
        {
          text: 'downloadIdSheet',
          icon: 'mdi-download',
          action: this.generatePdf,
          disabled: !this.account.isExternal
        },
        {
          text: 'downloadBol',
          icon: 'mdi-file-download-outline',
          action: this.downloadBol,
          disabled: this.billOfLading === undefined,
          hidden: !this.account.requireBol
        },
        {
          text: 'exportScalingConfig',
          icon: 'mdi-export',
          action: () => this.handleExportScalingConfig(),
          disabled: this.scalingExporting
        },
        {
          text: 'importScalingConfig',
          icon: 'mdi-import',
          action: () => this.triggerScalingImport(),
          disabled: this.scalingImporting || !userAssignedClaim(UserClaims.AccountManager)
        }
      ] : []
      return actions.filter(a => !a.hidden)
    },
    hasInsurancePolicy () {
      return this.account.insurancePolicies?.length > 0 || this.account.insuranceExpiryDate
    },
    // The EUDR tab lets an admin attach a supplier's EUDR file per period; it only applies to external accounts.
    showEudrTab () {
      return Boolean(this.account?.isExternal) && userAssignedClaim(UserClaims.AccountManager)
    },
    tabs () {
      const tabs = [
        this.$t('properties'),
        this.$t('contact'),
        this.$t('advances'),
        this.$t('insurance'),
        this.$t('certifications'),
        this.$t('payments'),
        this.$t('ticketBooks'),
        this.$t('products')
      ]
      // Appended last so the existing tab→window-item index alignment is preserved when present/absent.
      if (this.showEudrTab) tabs.push(this.$t('eudrDocuments'))
      return tabs
    },

    accountFileCategory () {
      return AccountFileCategory
    },

    penaltyHeaders () {
      return overweightThresholdHeaders()
    },

    invalidContactEmail () {
      return this.account.contacts.some(c => c.person.emailStatus === EmailStatus.Invalid.value)
    },

    propertyCardStyle () {
      return {
        minWidth: 'min(calc(100vw - 32px), 400px)'
      }
    }
  },

  async created () {
    this.loading = true
    await this.getAccount(this.accountId)
    await this.getFiles()
    if (this.account.requireBol) {
      this.billOfLading = await accountBolClient.fetchBolFromBlobStorage(this.account.accountId)
    }
  },

  mounted () {
    this.onMobileInverted()
    window.addEventListener('resize', this.onMobileInverted)
  },

  beforeDestroy () {
    document.removeEventListener('resize', this.onMobileInverted)
  },

  methods: {
    kebabCase,
    formatMoney,
    numberWithCommas,

    ...mapActions('snackbar', ['setSnackError', 'setSnack']),
    ...mapActions('account', ['fetchAccountById', 'fetchAccountPenaltyDefinition', 'fetchTicketBooks', 'patchAccount', 'updateAccount']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('account-scaling', ['fetchAccountLogLengths', 'fetchAccountLengthGroups', 'fetchAccountDiameterGroups', 'fetchAccountSpeciesGroups', 'createAccountLogLengths', 'createAccountLengthGroup', 'createAccountDiameterGroup', 'createAccountSpeciesGroup']),
    ...mapActions('rate-sheet-definition', ['fetchAccountRateSheetDefinitions', 'fetchVersionedRateSheetDefinition', 'createAccountRateSheetDefinition']),
    ...mapActions('product', ['fetchProducts']),
    ...mapActions('species', ['fetchSpecies']),
    ...mapActions('grade', ['fetchGrades']),
    ...mapActions('end-use-sort', ['fetchEndUseSorts']),
    async getAccount (accountId) {
      this.loading = true
      try {
        this.account = await this.fetchAccountById(accountId)
        if (this.account.penaltyDefinitionId) {
          this.penalty = await this.fetchAccountPenaltyDefinition(accountId)
        }
        this.account.scalingProducts = this.account.scalingProducts.map(p => ({ ...p, name: p.product }))
        this.account.fallbackProduct = {
          name: this.account.fallbackProduct,
          product: this.account.fallbackProduct,
          productId: this.account.fallbackProductId
        }
        this.pickupRadius = metersToMilesDiscrete(this.account?.receiverRadius)
        await this.refreshTicketBooks()
      } finally {
        this.loading = false
      }
    },

    async refreshTicketBooks () {
      this.ticketBooksLoading = true
      try {
        this.ticketBooks = await this.fetchTicketBooks({
          accountId: this.accountId,
          includeInUse: true,
          includeArchived: true
        })
      } finally {
        this.ticketBooksLoading = false
      }
    },

    checkAccountCode (account) {
      if (account.code) return true

      this.setSnackError(this.$t('missingQrAccountCode'))
      return false
    },

    showQrCode (account) {
      if (this.checkAccountCode(account)) this.$emit('show-qrcode', account)
    },

    showDriverQrCode (account) {
      this.$emit('show-driver-qrcode', account)
    },

    async generatePdf (account) {
      if (this.checkAccountCode(account)) {
        const text = getAccountQrCodeData(account, this.companyInfo)
        const qrCode = await getLegnaQrCodeUrl(250, text)
        generateAccountIdSheetPDF(account, qrCode, true)
      }
    },

    onMobileInverted () {
      if (window.innerWidth < MOBILE_BREAKPOINT) {
        this.mobileCompatible = true
        this.enlargeNavigationTabs = window.innerHeight > 600
      } else {
        this.mobileCompatible = false
      }
    },

    onTabClicked (t) {
      this.currentTab = t
    },

    async getFiles () {
      this.files = await accountFilesClient.getFilesForAccount(this.accountId)
    },

    filesByInsurancePolicy (insurancePolicy) {
      return this.files?.find(f => Number(f.policyId) === insurancePolicy.accountInsurancePolicyId && f.fileCategory === this.accountFileCategory.Insurance.value)
    },

    filesForCertifications () {
      return this.files?.filter(f => f.fileCategory === this.accountFileCategory.Certification.value)
    },

    async downloadBol () {
      const fetchResult = await fetch(this.billOfLading.ref)
      const blob = await fetchResult.blob()
      saveAs(blob, this.billOfLading.name)
    },

    closeDialog () {
      this.mapOpen = false
      this.scalingImportResultsOpen = false
      this.closeDialogsAtOrAbove(this.dialogId)
    },

    openDialog () {
      this.openOrUpdateDialog({ id: this.dialogId, width: '75vw' })
    },

    onReceiverMapClicked () {
      this.openDialog()
      this.mapOpen = true
      this.scalingImportResultsOpen = false
    },

    triggerScalingImport () {
      const fileInput = this.$refs.scalingFileInput
      if (fileInput) {
        fileInput.value = ''
        fileInput.click()
      }
    },

    async handleExportScalingConfig () {
      this.scalingExporting = true
      try {
        const accountId = this.account.accountId

        // Fetch all scaling data
        await this.fetchProducts()
        const logLengths = await this.fetchAccountLogLengths(accountId)
        const lengthGroups = await this.fetchAccountLengthGroups(accountId)
        const diameterGroups = await this.fetchAccountDiameterGroups(accountId)
        const speciesGroups = await this.fetchAccountSpeciesGroups(accountId)
        const rateSheetDefinitions = await this.fetchAccountRateSheetDefinitions(accountId)

        // Fetch versioned rates for each definition
        const fullDefinitions = await Promise.all(
          (rateSheetDefinitions || []).map(async defn => {
            const rates = await this.fetchVersionedRateSheetDefinition({
              accountId,
              rateSheetDefinitionId: defn.rateSheetDefinitionId
            })
            return { ...defn, rates }
          })
        )

        // Fetch species for name resolution
        await this.fetchSpecies()
        const allSpecies = this.$store.getters['species/allSpecies']

        await exportScalingSpecs({
          account: this.account,
          logLengths,
          lengthGroups,
          diameterGroups,
          speciesGroups,
          rateSheetDefinitions: fullDefinitions,
          allSpecies
        })

        this.setSnack(this.$t('scalingExportSuccess'))
      } catch (err) {
        this.setSnackError(err.message || this.$t('scalingTabFailed'))
      } finally {
        this.scalingExporting = false
      }
    },

    async onScalingFileSelected (event) {
      const file = event.target.files?.[0]
      if (!file) return

      this.scalingImporting = true
      try {
        const parsed = await parseAndVerifyImportFile(file)
        if (!parsed.valid) {
          this.setSnackError(this.$t(parsed.error))
          return
        }

        // Fetch reference data needed for import resolution
        await Promise.all([
          this.fetchProducts(),
          this.fetchSpecies(),
          this.fetchGrades(),
          this.fetchEndUseSorts()
        ])

        const allProducts = this.$store.getters['product/allProducts']
        const allSpecies = this.$store.getters['species/allSpecies']
        const allGrades = this.$store.getters['grade/allGrades']
        const allEndUseSorts = this.$store.getters['end-use-sort/allEndUseSorts']

        const results = await importScalingSpecs({
          data: parsed.data,
          account: this.account,
          allProducts,
          allSpecies,
          allGrades,
          allEndUseSorts,
          actions: {
            updateAccount: this.updateAccount,
            patchAccount: this.patchAccount,
            fetchAccountLogLengths: this.fetchAccountLogLengths,
            createAccountLogLengths: this.createAccountLogLengths,
            fetchAccountLengthGroups: this.fetchAccountLengthGroups,
            createAccountLengthGroup: this.createAccountLengthGroup,
            fetchAccountDiameterGroups: this.fetchAccountDiameterGroups,
            createAccountDiameterGroup: this.createAccountDiameterGroup,
            fetchAccountSpeciesGroups: this.fetchAccountSpeciesGroups,
            createAccountSpeciesGroup: this.createAccountSpeciesGroup,
            fetchAccountRateSheetDefinitions: this.fetchAccountRateSheetDefinitions,
            createAccountRateSheetDefinition: this.createAccountRateSheetDefinition
          }
        })

        this.scalingImportResults = results
        this.scalingImportResultsOpen = true
        this.mapOpen = false
        this.openOrUpdateDialog({ id: this.dialogId, width: '500px' })

        // Re-fetch account to reflect any changes
        await this.getAccount(this.accountId)
      } catch (err) {
        this.setSnackError(err.message || this.$t('scalingTabFailed'))
      } finally {
        this.scalingImporting = false
      }
    },

    closeScalingImportResults () {
      this.scalingImportResultsOpen = false
      this.scalingImportResults = []
      this.closeDialog()
    }
  }
}
</script>
